import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LiveResultsContext from './LiveResultsContext';
import { normalizeGroupValue } from '../utils/group';
import { ClientEngine } from '../../../resultsEngine/ClientEngine';

const DEFAULT_WS_URL = 'wss://www.admin.racepulse.pl:55127';
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
const RECONNECT_JITTER = 0.3;
// Brak jakiejkolwiek wiadomości (łącznie z PING) przez ten czas przy widocznej
// karcie oznacza półotwarty socket — wymuszamy reconnect.
const STALE_CONNECTION_MS = 60000;
const WATCHDOG_INTERVAL_MS = 15000;
// Seria delt w tym oknie daje jeden render.
const FLUSH_INTERVAL_MS = 250;
const EMPTY_RESULT = { stageResults: [], afterStageResults: [], isLoaded: false };

// Extracts InGroup string from a serialized groupKey.
// '["RC2"]' → "RC2",  '["WSZYSCY"]' or '' or null → null (no group filter).
function groupIdFromKey(groupKey) {
  if (!groupKey) return null;
  try {
    const arr = JSON.parse(groupKey);
    if (!Array.isArray(arr) || !arr.length) return null;
    const val = String(arr[0]);
    if (!val || val === 'WSZYSCY') return null;
    return val;
  } catch (_) {
    return null;
  }
}

const getWebSocketImpl = () => {
  if (typeof window !== 'undefined') {
    return window.WebSocket || window.MozWebSocket || null;
  }

  if (typeof WebSocket !== 'undefined') {
    return WebSocket;
  }

  return null;
};

const toKey = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value);
};

const sanitizeResults = (items) => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items.map((item) => ({
    ...item,
    Car: typeof item.Car === 'string' ? item.Car.replace(/\n/g, ' ').trim() : item.Car,
  }));
};

const buildGroupKeys = (view) => {
  const keys = new Set();

  const addKey = (value) => {
    const normalized = normalizeGroupValue(value);

    if (!normalized.length) {
      return;
    }

    keys.add(JSON.stringify(normalized));
  };

  addKey(view?.GroupListID ?? view?.Group ?? []);
  addKey(view?.Group);
  addKey(view?.GroupListID);

  return keys;
};

const indexViewsByClassification = (views = []) => {
  const index = {};

  views.forEach((view) => {
    if (!view) {
      return;
    }

    const classificationKey = toKey(view.ClassificationID);

    if (!classificationKey) {
      return;
    }

    const groupKeys = buildGroupKeys(view);

    if (!index[classificationKey]) {
      index[classificationKey] = {};
    }

    const cachedResults = {
      stageResults: sanitizeResults(view.StageResults),
      afterStageResults: sanitizeResults(view.AfterStageResults),
      isLoaded: true,
    };

    groupKeys.forEach((groupKey) => {
      if (!groupKey) {
        return;
      }

      index[classificationKey][groupKey] = cachedResults;
    });
  });

  return index;
};

const buildSocketUrl = (eventId) => {
  const template = process.env.REACT_APP_RESULTS_WS_URL || DEFAULT_WS_URL;

  if (eventId && template.includes('{eventId}')) {
    return template.replace('{eventId}', eventId);
  }

  return template;
};

const buildEventIdentifier = (eventId) => {
  if (eventId === null || eventId === undefined || eventId === '') {
    return null;
  }

  const numeric = Number(eventId);
  if (!Number.isNaN(numeric)) {
    return numeric;
  }

  return toKey(eventId);
};

const buildStageIdentifier = (stageId) => {
  if (stageId === null || stageId === undefined || stageId === '') {
    return null;
  }

  const numeric = Number(stageId);
  if (!Number.isNaN(numeric)) {
    return numeric;
  }

  return toKey(stageId);
};

const buildStageCacheKey = (eventKey, competitionId, stageId) => {
  const normalizedEventKey = toKey(eventKey);
  const competitionKey = toKey(competitionId);
  const stageKey = toKey(stageId);

  if (!normalizedEventKey || !competitionKey || !stageKey) {
    return null;
  }

  return `${normalizedEventKey}::${competitionKey}::${stageKey}`;
};

const LiveResultsProvider = ({ eventId, children }) => {
  const cacheRef = useRef(new Map());
  const clientEngineRef = useRef(null);          // ClientEngine instance when engine protocol active
  const stagePhasesRef = useRef(new Map());       // stageId → TimingPhase (from manual admin overrides)
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const lastMessageAtRef = useRef(Date.now());
  const flushTimerRef = useRef(null);
  const sendRequestRef = useRef(() => {});
  const lastRequestedStageIdRef = useRef(null);
  const [connectionError, setConnectionError] = useState(null);
  const [connectionState, setConnectionState] = useState('connecting');
  const [hasReceivedData, setHasReceivedData] = useState(false);
  const [version, setVersion] = useState(0);
  const [dataVersion, setDataVersion] = useState(0);
  const commentsRef = useRef([]);            // live komentarze, najnowszy pierwszy
  const [commentsVersion, setCommentsVersion] = useState(0);
  const normalizedEventKey = toKey(eventId);

  const MAX_COMMENTS = 100;

  const sortComments = (list) =>
    list.sort((a, b) => {
      const at = new Date(a.CommentTime).getTime() || 0;
      const bt = new Date(b.CommentTime).getTime() || 0;
      if (bt !== at) return bt - at;
      return (b.ID || 0) - (a.ID || 0);
    });

  const upsertComment = useCallback((comment) => {
    if (!comment || !comment.ID) return;
    const list = commentsRef.current.filter((c) => c.ID !== comment.ID);
    list.push(comment);
    commentsRef.current = sortComments(list).slice(0, MAX_COMMENTS);
    setCommentsVersion((v) => v + 1);
  }, []);

  const removeComment = useCallback((id) => {
    const before = commentsRef.current.length;
    commentsRef.current = commentsRef.current.filter((c) => c.ID !== id);
    if (commentsRef.current.length !== before) setCommentsVersion((v) => v + 1);
  }, []);

  // Seed z GET /api/public/liveComments — komentarze odebrane już po WS wygrywają.
  const seedComments = useCallback((rows) => {
    if (!Array.isArray(rows) || !rows.length) return;
    const known = new Set(commentsRef.current.map((c) => c.ID));
    const merged = commentsRef.current.concat(rows.filter((r) => r && r.ID && !known.has(r.ID)));
    commentsRef.current = sortComments(merged).slice(0, MAX_COMMENTS);
    setCommentsVersion((v) => v + 1);
  }, []);

  // Koalescencja renderów: delty aplikujemy do silnika natychmiast (tani zapis
  // do Map), ale dataVersion bumpujemy raz na okno — seria delt = jeden render.
  const scheduleFlush = useCallback((immediate = false) => {
    if (immediate) {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      setDataVersion((v) => v + 1);
      return;
    }
    if (flushTimerRef.current) return;
    flushTimerRef.current = setTimeout(() => {
      flushTimerRef.current = null;
      setDataVersion((v) => v + 1);
    }, FLUSH_INTERVAL_MS);
  }, []);

const mergeStageResults = useCallback(
    (payload, { eventId: overrideEventId } = {}) => {
      if (!payload) {
        return;
      }

      const stageId =
        payload.StageID ||
        payload.StageId ||
        payload.stageId ||
        payload.Stage?.StageID;

      const stageKey = toKey(stageId);

      if (!stageKey || !payload?.Views) {
        return;
      }

      const indexedViews = indexViewsByClassification(payload.Views);

      if (!Object.keys(indexedViews).length) {
        return;
      }

      const resolvedEventKey =
        normalizedEventKey ||
        toKey(
          overrideEventId ||
            payload.EventID ||
            payload.Stage?.EventID ||
            payload.Stage?.StageEventID
        );

      const cacheKey = buildStageCacheKey(
        resolvedEventKey,
        payload.CompetitionID,
        stageKey
      );

      if (!cacheKey) {
        return;
      }

      cacheRef.current.set(cacheKey, {
        competitionId: payload.CompetitionID,
        competitionName: payload.CompetitionName,
        stage: payload.Stage,
        views: indexedViews,
      });

      setHasReceivedData(true);
      setVersion((prev) => prev + 1);
    },
    [normalizedEventKey]
  );

  const handleRawMessage = useCallback(
    (rawMessage, { requestLatestResults } = {}) => {
      if (!rawMessage) {
        return;
      }

      let parsed;

      try {
        parsed = JSON.parse(rawMessage);
      } catch (error) {
        console.error('Błąd parsowania wiadomości WebSocket', error);
        return;
      }

      const messages = Array.isArray(parsed) ? parsed : [parsed];

      messages.forEach((message) => {
        if (!message) {
          return;
        }

        // Heartbeat aplikacyjny — sam odbiór odnotowany w lastMessageAtRef.
        if (message.type === 'PING') {
          return;
        }

        // ── Engine protocol messages ────────────────────────────────────────
        // Serwer może broadcastować delty wielu rajdów — aplikujemy tylko swoje.
        // Nowszy backend dodaje eventId do delt; fallback: znany stageId.
        const isOwnEngineDelta = (msg) => {
          if (msg.eventId !== undefined && msg.eventId !== null) {
            return toKey(msg.eventId) === normalizedEventKey;
          }
          return clientEngineRef.current?.hasStage(msg.stageId) ?? false;
        };

        if (message.type === 'ENGINE_BOOTSTRAP') {
          if (
            message.eventId !== undefined &&
            message.eventId !== null &&
            normalizedEventKey &&
            toKey(message.eventId) !== normalizedEventKey
          ) {
            return;
          }
          if (!clientEngineRef.current) clientEngineRef.current = new ClientEngine();
          clientEngineRef.current.applyBootstrap(message);
          // Bootstrap jest świeżo z DB — nadpisuje fazy odcinków.
          (message.stages || []).forEach((s) => {
            if (s?.timingPhase) stagePhasesRef.current.set(String(s.id), s.timingPhase);
          });
          setHasReceivedData(true);
          setVersion((prev) => prev + 1);
          scheduleFlush(true);
          return;
        }

        if (message.type === 'ENGINE_TIME_UPDATE') {
          if (clientEngineRef.current && isOwnEngineDelta(message)) {
            clientEngineRef.current.applyTimeUpdate(message);
            scheduleFlush();
          }
          return;
        }

        if (message.type === 'ENGINE_PENALTY_UPDATE') {
          if (clientEngineRef.current && isOwnEngineDelta(message)) {
            clientEngineRef.current.applyPenaltyUpdate(message);
            scheduleFlush();
          }
          return;
        }

        if (message.type === 'ENGINE_TEST_RUN_UPDATE') {
          if (clientEngineRef.current && isOwnEngineDelta(message)) {
            clientEngineRef.current.applyTestRunUpdate(message);
            scheduleFlush();
          }
          return;
        }

        // ── SP / legacy protocol messages ───────────────────────────────────
        // Skip full-packet SP messages when engine protocol is active
        if (clientEngineRef.current && clientEngineRef.current.isLoaded() &&
            (message.type === 'FULL_EVENT_RESULTS' || message.type === 'RESULT_UPDATE')) {
          return;
        }

        if (message.type === 'ERROR') {
          setConnectionError(message.message || 'Wystąpił błąd WebSocket.');
          return;
        }

        if (
          message.type === 'COMMENT_NEW' ||
          message.type === 'COMMENT_UPDATE' ||
          message.type === 'COMMENT_DELETE'
        ) {
          if (
            message.EventID !== undefined &&
            message.EventID !== null &&
            normalizedEventKey &&
            toKey(message.EventID) !== normalizedEventKey
          ) {
            return;
          }
          if (message.type === 'COMMENT_DELETE') {
            removeComment(Number(message.ID));
          } else if (message.comment) {
            upsertComment(message.comment);
          }
          return;
        }

        if (message.type === 'STAGE_PHASE_UPDATE') {
          if (
            message.EventID !== undefined &&
            message.EventID !== null &&
            normalizedEventKey &&
            toKey(message.EventID) !== normalizedEventKey
          ) {
            return;
          }
          if (message.StageID && message.TimingPhase) {
            stagePhasesRef.current.set(String(message.StageID), message.TimingPhase);
            clientEngineRef.current?.applyStagePhase(message.StageID, message.TimingPhase);
            setVersion((prev) => prev + 1);
          }
          return;
        }

        if (message.type && message.type !== 'FULL_EVENT_RESULTS' && message.type !== 'RESULT_UPDATE') {
          return;
        }

        const resultPayload = message.results || message;
        const eventIds = [
          message.EventID,
          resultPayload.EventID,
          resultPayload.Stage?.EventID,
          resultPayload.Stage?.StageEventID,
        ]
          .map(toKey)
          .filter(Boolean);

        const uniqueEventIds = Array.from(new Set(eventIds));
        const payloadEventId = uniqueEventIds[0];

        if (uniqueEventIds.length > 1) {
          return;
        }

        if (
          normalizedEventKey &&
          (!payloadEventId || payloadEventId !== normalizedEventKey)
        ) {
          return;
        }

        if (Array.isArray(resultPayload?.Competitions)) {
          resultPayload.Competitions.forEach((competition) => {
            if (!competition?.CompetitionID || !Array.isArray(competition?.Stages)) {
              return;
            }

            competition.Stages.forEach((stage) => {
              if (!stage) {
                return;
              }

              const { Views = [], ...stageMeta } = stage;
              const stageEventId = toKey(
                stageMeta.EventID || stageMeta.StageEventID || stageMeta.Stage?.EventID
              );

              if (payloadEventId && stageEventId && stageEventId !== payloadEventId) {
                return;
              }

              mergeStageResults(
                {
                  CompetitionID: competition.CompetitionID,
                  CompetitionName:
                    competition.NameCompetition ||
                    competition.ShortNameCompetition ||
                    competition.CompetitionName,
                  Stage: stageMeta,
                  StageID: stageMeta.StageID,
                  Views: Array.isArray(Views) ? Views : [],
                  EventID: resultPayload.EventID,
                },
                { eventId: payloadEventId }
              );
            });
          });
          return;
        }

        // RESULT_UPDATE with cascading recalculation (stage + all subsequent stages)
        if (Array.isArray(resultPayload.UpdatedStages) && resultPayload.UpdatedStages.length > 0) {
          resultPayload.UpdatedStages.forEach((updated) => {
            const { Views: updViews = [], ...updMeta } = updated;
            mergeStageResults(
              {
                CompetitionID:   resultPayload.CompetitionID,
                CompetitionName: resultPayload.CompetitionName,
                Stage:           updMeta,
                StageID:         Number(updated.StageID),
                Views:           updViews,
                EventID:         payloadEventId,
              },
              { eventId: payloadEventId }
            );
          });
          return;
        }

        const stageId =
          message.StageID ||
          resultPayload.StageID ||
          resultPayload.Stage?.StageID;

        if (!stageId || !resultPayload?.Views) {
          return;
        }

        mergeStageResults(
          {
            ...resultPayload,
            StageID: stageId,
          },
          { eventId: payloadEventId }
        );
      });
    },
    [mergeStageResults, normalizedEventKey, scheduleFlush, upsertComment, removeComment]
  );

  useEffect(() => {
    cacheRef.current = new Map();
    clientEngineRef.current = null;
    stagePhasesRef.current = new Map();
    commentsRef.current = [];
    setCommentsVersion((prev) => prev + 1);
    setHasReceivedData(false);
    setConnectionError(null);
    setVersion((prev) => prev + 1);
    sendRequestRef.current = () => {};
    lastRequestedStageIdRef.current = null;

    if (!eventId) {
      return undefined;
    }

    const WebSocketImpl = getWebSocketImpl();

    if (!WebSocketImpl) {
      setConnectionError('Ta przeglądarka nie obsługuje WebSocket.');
      setConnectionState('disconnected');
      return undefined;
    }

    let isActive = true;

    const clearReconnect = () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };

    const cleanupSocket = () => {
      if (socketRef.current) {
        try {
          // Odpinamy handlery przed zamknięciem — onclose porzucanego socketa
          // nie może zaplanować drugiego reconnectu.
          socketRef.current.onopen = null;
          socketRef.current.onmessage = null;
          socketRef.current.onerror = null;
          socketRef.current.onclose = null;
          socketRef.current.close();
        } catch (error) {
          console.error('Błąd przy zamykaniu połączenia WebSocket', error);
        }
        socketRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (!isActive) {
        return;
      }

      clearReconnect();
      // Wykładniczy backoff z jitterem — bez zsynchronizowanego młota
      // wszystkich klientów po restarcie serwera.
      const attempt = reconnectAttemptRef.current;
      const baseDelay = Math.min(RECONNECT_BASE_DELAY * 2 ** attempt, RECONNECT_MAX_DELAY);
      const jitter = baseDelay * RECONNECT_JITTER * (Math.random() * 2 - 1);
      const delay = Math.max(RECONNECT_BASE_DELAY, Math.round(baseDelay + jitter));
      reconnectAttemptRef.current = attempt + 1;
      reconnectTimeoutRef.current = setTimeout(() => {
        cleanupSocket();
        setConnectionState('connecting');
        connect();
      }, delay);
    };

    const sendRequest = (stageIdOverride = null) => {
      const socket = socketRef.current;
      if (!socket || socket.readyState !== (WebSocketImpl.OPEN ?? 1)) {
        return;
      }

      const payloadEventId = buildEventIdentifier(eventId);

      if (payloadEventId === null) {
        return;
      }

      // Always request bootstrap — backend replies with ENGINE_BOOTSTRAP (engine on)
      // or FULL_EVENT_RESULTS (engine off). Both handled in handleRawMessage.
      try {
        socket.send(JSON.stringify({ type: 'GET_BOOTSTRAP', EventID: payloadEventId }));
      } catch (error) {
        console.error('Nie udało się wysłać żądania wyników', error);
      }
    };

    sendRequestRef.current = sendRequest;

    const connect = () => {
      if (!isActive) {
        return;
      }

      const wsUrl = buildSocketUrl(eventId);

      try {
        socketRef.current = new WebSocketImpl(wsUrl);
        setConnectionState('connecting');
      } catch (error) {
        console.error('Nie udało się otworzyć połączenia WebSocket', error);
        setConnectionError('Nie udało się nawiązać połączenia WebSocket.');
        setConnectionState('disconnected');
        scheduleReconnect();
        return;
      }

      socketRef.current.onopen = () => {
        if (!isActive) {
          return;
        }

        reconnectAttemptRef.current = 0;
        lastMessageAtRef.current = Date.now();
        setConnectionError(null);
        setConnectionState('connected');
        sendRequest();
      };

      socketRef.current.onmessage = (event) => {
        if (!isActive) {
          return;
        }

        lastMessageAtRef.current = Date.now();
        handleRawMessage(event.data, { requestLatestResults: sendRequest });
      };

      socketRef.current.onerror = () => {
        if (!isActive) {
          return;
        }

        setConnectionError('Nie udało się połączyć z serwerem wyników.');
        setConnectionState('disconnected');
      };

      socketRef.current.onclose = () => {
        if (!isActive) {
          return;
        }

        setConnectionState('disconnected');
        scheduleReconnect();
      };
    };

    const reconnectNow = () => {
      if (!isActive) return;
      reconnectAttemptRef.current = 0;
      const socket = socketRef.current;
      const isOpen = socket && socket.readyState === (WebSocketImpl.OPEN ?? 1);
      if (isOpen) {
        sendRequest();
        return;
      }
      clearReconnect();
      cleanupSocket();
      setConnectionState('connecting');
      connect();
    };

    // Watchdog: Firefox Android potrafi zostawić półotwarty socket po
    // uśpieniu — onclose nigdy nie przychodzi, a aktualizacje cichną.
    // Brak ruchu (serwer śle PING co ~25 s) przez STALE_CONNECTION_MS przy
    // widocznej karcie → wymuszamy pełny reconnect.
    const watchdogInterval = setInterval(() => {
      if (!isActive || document.hidden) return;
      const socket = socketRef.current;
      const isOpen = socket && socket.readyState === (WebSocketImpl.OPEN ?? 1);
      if (!isOpen) return;
      if (Date.now() - lastMessageAtRef.current > STALE_CONNECTION_MS) {
        try {
          socket.close();
        } catch (error) {
          console.error('Błąd przy zamykaniu martwego połączenia WebSocket', error);
        }
      }
    }, WATCHDOG_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        reconnectNow();
      }
    };

    const handleOnline = () => {
      reconnectNow();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnline);

    connect();

    return () => {
      isActive = false;
      clearReconnect();
      clearInterval(watchdogInterval);
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      cleanupSocket();
      setConnectionState('disconnected');
      sendRequestRef.current = () => {};
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnline);
    };
  }, [eventId, handleRawMessage]);

  const getResultsForSelection = useCallback(
    (competitionId, stageId, classificationId, groupKey) => {
      if (!competitionId || !stageId || !classificationId) {
        return EMPTY_RESULT;
      }

      // ── Engine protocol path ────────────────────────────────────────────────
      if (clientEngineRef.current && clientEngineRef.current.isLoaded()) {
        const groupId = groupIdFromKey(groupKey);
        return {
          stageResults:      clientEngineRef.current.getStageResults(stageId, classificationId, groupId),
          afterStageResults: clientEngineRef.current.getAfterStageResults(stageId, classificationId, groupId),
          isLoaded: true,
        };
      }

      // ── SP / legacy protocol path ───────────────────────────────────────────
      const cacheKey = buildStageCacheKey(
        normalizedEventKey,
        competitionId,
        stageId
      );

      if (!cacheKey) {
        return EMPTY_RESULT;
      }

      const stageEntry = cacheRef.current.get(cacheKey);

      if (!stageEntry) {
        return EMPTY_RESULT;
      }

      const classificationViews = stageEntry.views[toKey(classificationId)];

      if (!classificationViews) {
        return EMPTY_RESULT;
      }

      if (groupKey && classificationViews[groupKey]) {
        return classificationViews[groupKey];
      }

      const fallbackKey = Object.keys(classificationViews)[0];

      if (fallbackKey) {
        return classificationViews[fallbackKey];
      }

      return EMPTY_RESULT;
    },
    // dataVersion celowo w deps: wymusza nową tożsamość selektora po każdej
    // (skoalescowanej) delcie silnika, żeby konsumenci przeliczyli wyniki.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [normalizedEventKey, dataVersion]
  );

  const requestStageResults = useCallback((stageId) => {
    if (!stageId) {
      return;
    }

    lastRequestedStageIdRef.current = stageId;

    if (typeof sendRequestRef.current === 'function') {
      try {
        sendRequestRef.current(stageId);
      } catch (error) {
        console.error('Nie udało się poprosić o wyniki OS', error);
      }
    }
  }, []);

  const getStageTimingPhase = useCallback(
    (competitionId, stageId) => {
      // Fazy live (bootstrap + STAGE_PHASE_UPDATE) mają pierwszeństwo
      const live = stagePhasesRef.current.get(String(stageId));
      if (live) return live;
      // Faza z danych silnika (bootstrap engine)
      const enginePhase = clientEngineRef.current?.getStagePhase(stageId);
      if (enginePhase) return enginePhase;
      // Fall back to phase from last RESULT_UPDATE
      const cacheKey = buildStageCacheKey(normalizedEventKey, competitionId, stageId);
      if (!cacheKey) return null;
      return cacheRef.current.get(cacheKey)?.stage?.TimingPhase ?? null;
    },
    [normalizedEventKey]
  );

  // Live komentarze (WS + seed z REST), najnowszy pierwszy.
  const comments = useMemo(() => commentsRef.current.slice(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commentsVersion]);

  // Dane załogi po numerze startowym — do tooltipów #numer w komentarzach.
  const getEntryByNumber = useCallback(
    (positionNumber) => clientEngineRef.current?.getEntryByNumber(positionNumber) ?? null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataVersion]
  );

  // Płaski obiekt stageId → TimingPhase dla osi czasu / arkuszy mobilnych.
  // Przebudowywany, gdy przyjdzie STAGE_PHASE_UPDATE lub bootstrap (version).
  const stagePhases = useMemo(() => {
    const phases = {};
    stagePhasesRef.current.forEach((phase, stageId) => {
      phases[stageId] = phase;
    });
    return phases;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const contextValue = useMemo(
    () => ({
      getResultsForSelection,
      getStageTimingPhase,
      stagePhases,
      comments,
      seedComments,
      getEntryByNumber,
      loading: !hasReceivedData,
      error: connectionError,
      version,
      dataVersion,
      connectionState,
      requestStageResults,
    }),
    [
      comments,
      connectionError,
      connectionState,
      dataVersion,
      getEntryByNumber,
      getResultsForSelection,
      getStageTimingPhase,
      hasReceivedData,
      requestStageResults,
      seedComments,
      stagePhases,
      version,
    ]
  );

  return (
    <LiveResultsContext.Provider value={contextValue}>
      {children}
    </LiveResultsContext.Provider>
  );
};

export default LiveResultsProvider;
